import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { creativeStrategyApi, type StrategyDocketExemplarPage, type StrategyExemplarRecord } from '@/services/api';
import { ExemplarCard, ExemplarLightbox } from './Exemplars';
import { Empty, ErrorNote, Loading, WarningBanner, formatDate, toText } from './shared';

const PAGE = 48;

/** Gallery of the ads behind a docket's strategies and templates. */
export function DocketExemplarsTab({
  docketId,
  version,
  onOpenItem,
}: {
  docketId: string;
  version?: string | null;
  onOpenItem: (id: string) => void;
}) {
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [advertiser, setAdvertiser] = useState('');
  const [layout, setLayout] = useState('');
  const [citedOnly, setCitedOnly] = useState(true);
  const [mediaOnly, setMediaOnly] = useState(false);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<StrategyDocketExemplarPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<StrategyExemplarRecord | null>(null);
  // Index notes (e.g. an interim-source warning) live on the version detail.
  const [indexNotes, setIndexNotes] = useState<string[]>([]);

  useEffect(() => {
    if (!version) return;
    let cancelled = false;
    creativeStrategyApi
      .docketVersion(docketId, version)
      .then((v) => !cancelled && setIndexNotes(v.exemplar_index?.notes || []))
      .catch(() => undefined); // notes are best-effort; the gallery still works
    return () => {
      cancelled = true;
    };
  }, [docketId, version]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(qInput.trim());
      setOffset(0);
    }, 300);
    return () => clearTimeout(t);
  }, [qInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    creativeStrategyApi
      .docketExemplars(docketId, {
        version,
        q,
        advertiser: advertiser || null,
        layout: layout || null,
        citedOnly,
        mediaOnly,
        offset,
        limit: PAGE,
      })
      .then((p) => !cancelled && setPage(p))
      .catch((err: Error) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [docketId, version, q, advertiser, layout, citedOnly, mediaOnly, offset]);

  const closeLightbox = useCallback(() => setLightbox(null), []);
  const resetPage = () => setOffset(0);

  const idx = page?.index;
  const advertisers = (page?.facets.advertisers || []).filter(([name]) => name);
  const layouts = (page?.facets.layouts || []).filter(([name]) => name);
  // Keep a selected facet visible even when the current filter narrows it away.
  if (advertiser && !advertisers.some(([n]) => n === advertiser)) advertisers.unshift([advertiser, 0]);
  if (layout && !layouts.some(([n]) => n === layout)) layouts.unshift([layout, 0]);
  const total = page?.total ?? 0;

  return (
    <div className="space-y-4">
      {indexNotes.map((n, i) => (
        <WarningBanner key={i}>{n}</WarningBanner>
      ))}
      {idx && !idx.available && (
        <WarningBanner>
          No exemplar index is available for this docket yet — cards show ad keys only (no image, copy or lens). If an upload is in progress, refresh in a few minutes.
        </WarningBanner>
      )}
      {idx && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
          <span>
            index: <span className={idx.available ? 'text-emerald-700' : 'text-amber-700'}>{idx.available ? 'available' : 'unavailable'}</span>
          </span>
          <span>{idx.ads.toLocaleString()} ads indexed</span>
          <span>
            {idx.cited_resolved.toLocaleString()} / {idx.cited_keys.toLocaleString()} cited ads resolved
          </span>
          {idx.generated_at && <span>built {formatDate(idx.generated_at)}</span>}
          {idx.sources.length > 0 && <span className="truncate max-w-full">sources: {idx.sources.map(toText).join(', ')}</span>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search advertiser, copy, lens…"
            className="text-sm border border-slate-300 rounded-lg pl-7 pr-2 py-1 w-64"
          />
        </div>
        <select
          className="text-sm border border-slate-300 rounded-lg px-2 py-1 max-w-[220px]"
          value={advertiser}
          onChange={(e) => {
            setAdvertiser(e.target.value);
            resetPage();
          }}
          aria-label="Advertiser"
        >
          <option value="">All advertisers</option>
          {advertisers.map(([name, n]) => (
            <option key={name} value={name}>{name}{n ? ` (${n})` : ''}</option>
          ))}
        </select>
        <select
          className="text-sm border border-slate-300 rounded-lg px-2 py-1 max-w-[220px]"
          value={layout}
          onChange={(e) => {
            setLayout(e.target.value);
            resetPage();
          }}
          aria-label="Layout"
        >
          <option value="">All layouts</option>
          {layouts.map(([name, n]) => (
            <option key={name} value={name}>{name}{n ? ` (${n})` : ''}</option>
          ))}
        </select>
        <label className="text-xs text-slate-600 inline-flex items-center gap-1">
          <input type="checkbox" checked={citedOnly} onChange={(e) => { setCitedOnly(e.target.checked); resetPage(); }} />
          cited only
        </label>
        <label className="text-xs text-slate-600 inline-flex items-center gap-1">
          <input type="checkbox" checked={mediaOnly} onChange={(e) => { setMediaOnly(e.target.checked); resetPage(); }} />
          with image only
        </label>
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
          {page && (
            <span>
              {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE, total)} of {total.toLocaleString()}
            </span>
          )}
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="p-1 rounded border border-slate-300 disabled:opacity-40"
            aria-label="Previous page"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            type="button"
            disabled={offset + PAGE >= total || loading}
            onClick={() => setOffset(offset + PAGE)}
            className="p-1 rounded border border-slate-300 disabled:opacity-40"
            aria-label="Next page"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {error && <ErrorNote error={error} />}
      {loading && !page && <Loading label="Loading exemplar ads…" />}
      {page && page.exemplars.length === 0 && !loading && <Empty>No exemplar ads match.</Empty>}
      {page && page.exemplars.length > 0 && (
        <div className={loading ? 'opacity-60 transition-opacity' : undefined}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {page.exemplars.map((r) => (
              <ExemplarCard key={r.ad_key} record={r} onOpen={setLightbox} onOpenItem={onOpenItem} showLens={false} />
            ))}
          </div>
        </div>
      )}
      <ExemplarLightbox record={lightbox} onClose={closeLightbox} onOpenItem={onOpenItem} />
    </div>
  );
}
